import React from 'react';
import { clsx } from 'clsx';

interface FileIconProps {
  fileName: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

/**
 * Get file extension from filename
 */
const getFileExtension = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase() || 'unknown';
  return extension;
};

/**
 * Map file extension to available icon image
 * Matches Angular implementation: assets/images/file/{{file.fileExt}}.png
 */
const getIconFileName = (extension: string): string => {
  // Map of extensions to icon files
  const extensionMap: Record<string, string> = {
    // PDF
    pdf: 'pdf',
    // Excel variants
    xlsx: 'xlsx',
    xls: 'xls',
    xlsm: 'xlsm',
    // Word variants
    doc: 'doc',
    docx: 'doc',
    // CSV
    csv: 'csv',
    // PowerPoint
    ppt: 'ppt',
    pptx: 'ppt',
    // Images
    png: 'png',
    jpg: 'png',
    jpeg: 'png',
    gif: 'png',
    // Default
    unknown: 'unknown',
  };

  return extensionMap[extension] || 'unknown';
};

const getSizeClasses = (size: 'sm' | 'md' | 'lg' | 'xl') => {
  const sizes = {
    sm: 'h-6 w-6',
    md: 'h-10 w-10',
    lg: 'h-16 w-16',
    xl: 'h-20 w-20',
  };

  return sizes[size];
};

export const FileIcon: React.FC<FileIconProps> = ({ fileName, size = 'md', className }) => {
  const extension = getFileExtension(fileName);
  const iconFile = getIconFileName(extension);
  const sizeClass = getSizeClasses(size);

  return (
    <div className={clsx('flex items-center justify-center', sizeClass, className)}>
      <img
        src={`/assets/images/file/${iconFile}.png`}
        alt={`${extension.toUpperCase()} file`}
        className="h-full w-full object-contain"
        onError={(e) => {
          // Fallback to unknown icon if image fails to load
          const target = e.target as HTMLImageElement;
          if (!target.src.includes('unknown.png')) {
            target.src = '/assets/images/file/unknown.png';
          }
        }}
      />
    </div>
  );
};
